import React, { useCallback, useEffect, useState } from "react";
import { apiFetch } from "./api";

export default function StaffAccessSettings() {
  const [staff, setStaff] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadStaff = useCallback(async () => {
    try {
      const response = await apiFetch("/staff/owner");
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unable to load staff access.");
      }

      setStaff(data.staff || null);
      setError("");
    } catch (err) {
      setError(err.message || "Unable to load staff access.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const inviteStaff = async (event) => {
    event.preventDefault();
    if (busy || staff) return;

    setBusy(true);
    setError("");
    setSuccess("");

    try {
      const response = await apiFetch("/staff/owner/invite", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unable to send invitation.");
      }

      setName("");
      setEmail("");
      setSuccess("Staff invitation sent successfully.");
      await loadStaff();
    } catch (err) {
      setError(err.message || "Unable to invite staff.");
    } finally {
      setBusy(false);
    }
  };

  const revokeStaff = async () => {
    if (!staff || busy) return;

    const confirmed = window.confirm(
      "Revoke this staff member's CRM access? " +
      "Their MUKBI staff login will stop working immediately. " +
      "WhatsApp Business mobile-app access must be managed separately."
    );

    if (!confirmed) return;

    setBusy(true);
    setError("");
    setSuccess("");

    try {
      const response = await apiFetch("/staff/owner/revoke", {
        method: "POST"
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unable to revoke staff access.");
      }

      setStaff(null);
      setSuccess("Staff access revoked. You can now invite a replacement.");
    } catch (err) {
      setError(err.message || "Unable to revoke staff access.");
    } finally {
      setBusy(false);
    }
  };

  const field = {
    width: "100%",
    boxSizing: "border-box",
    padding: 11,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    marginTop: 6,
    marginBottom: 15
  };

  return (
    <section style={{
      maxWidth: 720,
      margin: "25px auto",
      padding: 24,
      background: "white",
      border: "1px solid #e2e8f0",
      borderRadius: 12
    }}>
      <h2>Staff Access</h2>
      <p style={{ color: "#64748b" }}>
        Invite one staff member to help manage customer information,
        follow-ups and notes. WhatsApp API messaging and account
        administration remain with the CRM owner.
      </p>

      {error && <p role="alert" style={{ color: "#b91c1c" }}>{error}</p>}
      {success && <p role="status" style={{ color: "#15803d" }}>{success}</p>}

      {loading ? (
        <p>Loading staff access...</p>
      ) : staff ? (
        <div style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          padding: 18,
          borderRadius: 9
        }}>
          <p><strong>Name:</strong> {staff.name}</p>
          <p><strong>Email:</strong> {staff.email}</p>
          <p><strong>Status:</strong> {
            staff.status === "active" ? "Active" : "Invitation Pending"
          }</p>

          <button
            type="button"
            disabled={busy}
            onClick={revokeStaff}
            style={{
              padding: "10px 16px",
              border: "1px solid #dc2626",
              borderRadius: 7,
              background: "white",
              color: "#b91c1c",
              cursor: busy ? "wait" : "pointer"
            }}
          >
            {busy ? "Processing..." : "Revoke Staff Access"}
          </button>
        </div>
      ) : (
        <form onSubmit={inviteStaff}>
          <label htmlFor="staff-invite-name">Staff Name</label>
          <input
            id="staff-invite-name"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            maxLength={100}
            style={field}
          />

          <label htmlFor="staff-invite-email">Staff Email</label>
          <input
            id="staff-invite-email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            style={field}
          />

          <button
            type="submit"
            disabled={busy}
            style={{
              padding: "11px 20px",
              background: "#2c3e50",
              color: "white",
              border: "none",
              borderRadius: 7,
              cursor: busy ? "wait" : "pointer"
            }}
          >
            {busy ? "Sending Invitation..." : "Send Staff Invitation"}
          </button>
        </form>
      )}

      <p style={{ fontSize: 12, color: "#64748b", marginTop: 20 }}>
        Staff Access is available while your CRM subscription,
        trial or complimentary access is active. Revoking MUKBI
        access does not disconnect WhatsApp Business linked devices.
      </p>
    </section>
  );
}
